// The map's derived runtime form: everything gameplay and the view read about the terrain. The data
// itself lives in maps/<name>.json (see core/map/) — this module expands it and answers walkability/sight.
import { NavGrid } from './nav.js';
import type { MonsterKind } from './monsters.js';
import { ACTIVE_MAP } from './map/active.js';
import { expandMap } from './map/expand.js';
import { distSeg, distPolyline } from './map/geometry.js';
import type { Building, Cliff, Decor, MapData, Patch, Prop, PropKind, Road, Wall } from './map/schema.js';

export type Pt = [number, number];
export type Team = 'a' | 'b';

const distRing = (px: number, pz: number, pts: Pt[]) => Math.min(distPolyline(px, pz, pts), distSeg(px, pz, pts[pts.length - 1] as Pt, pts[0] as Pt));
function inPolygon(x: number, z: number, pts: Pt[]) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, zi] = pts[i] as Pt, [xj, zj] = pts[j] as Pt;
    if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}
/* Inside an oriented rectangle (a building, field or ford) grown by `grow` on every side. */
export function inRect(x: number, z: number, b: Pick<Building, 'x' | 'z' | 'w' | 'd' | 'rot'>, grow = 0) {
  const c = Math.cos(b.rot), s = Math.sin(b.rot), dx = x - b.x, dz = z - b.z;
  const lx = dx * c + dz * s, lz = -dx * s + dz * c;
  return Math.abs(lx) <= b.w / 2 + grow && Math.abs(lz) <= b.d / 2 + grow;
}
const segments = (pts: Pt[]): [Pt, Pt][] => pts.slice(1).map((p, i): [Pt, Pt] => [pts[i] as Pt, p]);

export type LayoutPoint = { id: string; pos: Pt };
export type Layout = {
  name: string;
  bounds: { x: number; z: number };
  bases: Record<Team, Pt>;
  points: LayoutPoint[];
  bosses: { kind: MonsterKind; pos: Pt }[];
  bridges: Pt[];
  pillars: Pt[];
  /** Every wall segment, any style — what collision reads. `fences` keeps the styled polylines. */
  walls: [Pt, Pt][];
  fences: Wall[];
  /** Every road segment; `roadPaths` keeps the polylines with their widths. */
  roads: [Pt, Pt][];
  roadPaths: Road[];
  pointRadius: number;
  camps: Pt[];
  streams: Pt[][];
  streamWidths: number[];  // per stream, same index
  brush: [number, number, number, number, number][];
  trees: Pt[];
  rocks: Pt[];
  buildings: Building[];
  ponds: Pt[][];
  cliffs: Cliff[];
  decor: Decor[];
  props: Prop[];
  fields: Patch[];
  fords: Patch[];
};

export function layoutFromMap(map: MapData): Layout {
  const e = expandMap(map), o = e.objects;
  return {
    name: e.name,
    bounds: e.bounds,
    bases: e.bases,
    points: e.points,
    bosses: e.dens,
    bridges: o.bridges,
    pillars: o.pillars,
    walls: o.walls.flatMap(w => segments(w.points)),
    fences: o.walls,
    roads: o.roads.flatMap(r => segments(r.points)),
    roadPaths: o.roads,
    pointRadius: e.pointRadius,
    camps: o.camps,
    streams: o.streams.map(s => s.points),
    streamWidths: o.streams.map(s => s.width),
    brush: o.brush.map(b => [b.x, b.z, b.rx, b.rz, b.rot]),
    trees: o.trees,
    rocks: o.rocks,
    buildings: o.buildings,
    ponds: o.ponds,
    cliffs: o.cliffs,
    decor: o.decor,
    props: o.props,
    fields: o.fields,
    fords: o.fords,
  };
}

const HERO_R = 0.45;

/** Each prop's footprint and what it stops. The footprint is the body itself; movement keeps a hero's radius
    clear of it. A haystack is tall and dense like a tree crown; the cart is cover like a rock (shots hit
    its bed and barrels) but low enough to see over; a cross and a well are low stonework shots fly over. */
export const PROP_SHAPES: Record<PropKind, { r?: number; w?: number; d?: number; solid: boolean; sight: boolean }> = {
  haystack: { r: 1.1, solid: true, sight: true },
  cart: { w: 2.6, d: 1.4, solid: true, sight: false },
  cross: { r: 0.45, solid: false, sight: false },
  well: { r: 1.0, solid: false, sight: false },
};
function inProp(x: number, z: number, p: Prop, grow = 0) {
  const s = PROP_SHAPES[p.kind];
  return s.r !== undefined ? Math.hypot(x - p.x, z - p.z) < s.r + grow : inRect(x, z, { x: p.x, z: p.z, w: s.w!, d: s.d!, rot: p.rot }, grow);
}
/** Walking speed in a ford: 20% slower. */
export const FORD_SPEED = 0.8;

export type BaseParts = {
  fountain: Pt;
  flag: Pt;
  shop: Pt;
  spawn: (k: number) => Pt;
  facing: number;
};

/* Base furniture sits on the corner side; heroes spawn on the side facing the map. */
export function baseParts(team: Team, L: Layout = LAYOUT): BaseParts {
  const [x, z] = L.bases[team], sx = Math.sign(x), sz = Math.sign(z);
  const len = Math.hypot(x, z), toCenter = [-x / len, -z / len];
  return {
    fountain: [x, z],
    flag: [x, z + sz * 2.9],
    shop: [x + sx * 2.8, z],  // on the corner side of the floor, away from the spawn
    spawn: (k: number): Pt => {
      const side = k % 2 ? 1 : -1;
      return [x + (toCenter[0] as number) * 2.6 - (toCenter[1] as number) * side * 1.1, z + (toCenter[1] as number) * 2.6 + (toCenter[0] as number) * side * 1.1];
    },
    facing: Math.atan2(-x, -z),
  };
}
export function inEllipse(x: number, z: number, [cx, cz, rx, rz, rot]: [number, number, number, number, number], grow = 0) {
  const c = Math.cos(-rot), s = Math.sin(-rot), dx = x - cx, dz = z - cz;
  const lx = dx * c - dz * s, lz = dx * s + dz * c;
  return (lx / (rx + grow)) ** 2 + (lz / (rz + grow)) ** 2 <= 1;
}
/** The active map's layout; rebound (with the terrain functions below) by core/map/runtime.ts's setActiveMap(). */
export let LAYOUT: Layout = layoutFromMap(ACTIVE_MAP);
export const inBrush = (x: number, z: number) => LAYOUT.brush.some(b => inEllipse(x, z, b));
export type VisionSource = { x: number; z: number; r: number; through?: boolean };

const CLIFF_HALF = 0.5;  // an open cliff (a ledge line) is this thick on each side

/* Walkability and sight for one layout: nav, hard cover and line of sight. The module-level exports below
   are this bound to the active map (rebound by bindLayout()); tests build it for their own maps. */
export function terrain(L: Layout) {
  /* ---- line of sight: tree crowns, haystacks and buildings block vision (like Dota); rocks, ruins, walls, water and cliffs do not ---- */
  // Sight rays march thousands of steps per update, so the blockers are baked once into a raster of cells.
  const TREE_BLOCK = 1.05, CELL = 0.25;
  const RX = Math.ceil(2 * L.bounds.x / CELL) + 1, RZ = Math.ceil(2 * L.bounds.z / CELL) + 1;
  const sightRaster = new Uint8Array(RX * RZ);
  const markCells = (cx: number, cz: number, span: number, hit: (px: number, pz: number) => boolean) => {
    for (let i = cx - span; i <= cx + span; i++) for (let j = cz - span; j <= cz + span; j++) {
      if (i < 0 || j < 0 || i >= RX || j >= RZ) continue;
      if (hit(i * CELL - L.bounds.x, j * CELL - L.bounds.z)) sightRaster[i + j * RX] = 1;
    }
  };
  for (const [x, z] of L.trees) {
    markCells(Math.round((x + L.bounds.x) / CELL), Math.round((z + L.bounds.z) / CELL), Math.ceil(TREE_BLOCK / CELL),
      (px, pz) => (px - x) ** 2 + (pz - z) ** 2 < TREE_BLOCK * TREE_BLOCK);
  }
  for (const b of L.buildings) {
    markCells(Math.round((b.x + L.bounds.x) / CELL), Math.round((b.z + L.bounds.z) / CELL), Math.ceil(Math.hypot(b.w, b.d) / 2 / CELL) + 1,
      (px, pz) => inRect(px, pz, b));
  }
  for (const p of L.props) {
    const s = PROP_SHAPES[p.kind];
    if (!s.sight) continue;
    const reach = s.r ?? Math.hypot(s.w!, s.d!) / 2;
    markCells(Math.round((p.x + L.bounds.x) / CELL), Math.round((p.z + L.bounds.z) / CELL), Math.ceil(reach / CELL) + 1, (px, pz) => inProp(px, pz, p));
  }
  function sightBlockedAt(x: number, z: number) {
    const i = Math.round((x + L.bounds.x) / CELL), j = Math.round((z + L.bounds.z) / CELL);
    return i >= 0 && j >= 0 && i < RX && j < RZ && sightRaster[i + j * RX] === 1;
  }
  /* Distance a sight ray travels from (x, z) along angle before a blocker stops it (the first tree itself stays visible). */
  function rayReach(x: number, z: number, ang: number, r: number) {
    const dx = Math.cos(ang), dz = Math.sin(ang);
    for (let d = 0.6; d < r; d += 0.35) {
      if (sightBlockedAt(x + dx * d, z + dz * d)) return Math.min(r, d + 0.9);
    }
    return r;
  }
  function lineOfSight(ax: number, az: number, bx: number, bz: number) {
    const len = Math.hypot(bx - ax, bz - az);
    if (len < 1.2) return true;
    for (let d = 0.6; d < len - 0.6; d += 0.35) {
      const k = d / len;
      if (sightBlockedAt(ax + (bx - ax) * k, az + (bz - az) * k)) return false;
    }
    return true;
  }
  /* Vision polygon of a source: points on the rim of what it can see, for the fog texture and minimap. */
  function visionPolygon(src: VisionSource, rays = 160): Pt[] {
    const pts: Pt[] = [];
    for (let i = 0; i < rays; i++) {
      const a = (i / rays) * Math.PI * 2;
      const d = src.through ? src.r : rayReach(src.x, src.z, a, src.r);
      pts.push([src.x + Math.cos(a) * d, src.z + Math.sin(a) * d]);
    }
    return pts;
  }
  /* Hard cover: what stops skillshots and what a knocked-back hero slams into (water, cliffs and bounds do not count). */
  function solid(x: number, z: number) {
    return L.pillars.some(([px, pz]) => Math.hypot(x - px, z - pz) < 0.7) ||
      L.walls.some(([a, b]) => distSeg(x, z, a, b) < 0.5) ||
      L.trees.some(([px, pz]) => Math.hypot(x - px, z - pz) < 0.55) ||
      L.rocks.some(([px, pz]) => Math.hypot(x - px, z - pz) < 0.9) ||
      L.buildings.some(b => inRect(x, z, b)) ||
      L.props.some(p => PROP_SHAPES[p.kind].solid && inProp(x, z, p));
  }
  /* solid(), limited to the obstacles that reach into the circle (cx, cz, r) — for a caller sampling many points
     there (Связка's anchor search); null when none does. */
  function solidNear(cx: number, cz: number, r: number): ((x: number, z: number) => boolean) | null {
    const pillars = L.pillars.filter(([px, pz]) => Math.hypot(cx - px, cz - pz) < r + 0.7);
    const walls = L.walls.filter(([a, b]) => distSeg(cx, cz, a, b) < r + 0.5);
    const trees = L.trees.filter(([px, pz]) => Math.hypot(cx - px, cz - pz) < r + 0.55);
    const rocks = L.rocks.filter(([px, pz]) => Math.hypot(cx - px, cz - pz) < r + 0.9);
    const buildings = L.buildings.filter(b => inRect(cx, cz, b, r));
    const props = L.props.filter(p => PROP_SHAPES[p.kind].solid && inProp(cx, cz, p, r));
    if (!pillars.length && !walls.length && !trees.length && !rocks.length && !buildings.length && !props.length) return null;
    return (x, z) => pillars.some(([px, pz]) => Math.hypot(x - px, z - pz) < 0.7) ||
      walls.some(([a, b]) => distSeg(x, z, a, b) < 0.5) ||
      trees.some(([px, pz]) => Math.hypot(x - px, z - pz) < 0.55) ||
      rocks.some(([px, pz]) => Math.hypot(x - px, z - pz) < 0.9) ||
      buildings.some(b => inRect(x, z, b)) ||
      props.some(p => inProp(x, z, p));
  }
  /* Exported for the client's grass-tuft scatter (map.js used the same predicate for both); gameplay
     code should use buildNav()'s NavGrid, not call this per-query. */
  function blocked(x: number, z: number) {
    if (Math.abs(x) > L.bounds.x || Math.abs(z) > L.bounds.z) return true;
    const onBridge = L.bridges.some(([bx, bz]) => Math.hypot(x - bx, z - bz) < 2.5);
    // a ford crosses a stream only: a pond under one stays water
    if (!onBridge && !L.fords.some(f => inRect(x, z, f)) && L.streams.some((s, i) => distPolyline(x, z, s) < (L.streamWidths[i] as number) / 2 + HERO_R)) return true;
    if (!onBridge && L.ponds.some(p => inPolygon(x, z, p) || distRing(x, z, p) < HERO_R)) return true;
    if (L.pillars.some(([px, pz]) => Math.hypot(x - px, z - pz) < 0.55 + HERO_R)) return true;
    if (L.walls.some(([a, b]) => distSeg(x, z, a, b) < 0.35 + HERO_R)) return true;
    if (L.trees.some(([px, pz]) => Math.hypot(x - px, z - pz) < 0.45 + HERO_R)) return true;
    if (L.rocks.some(([px, pz]) => Math.hypot(x - px, z - pz) < 0.8 + HERO_R)) return true;
    if (L.buildings.some(b => inRect(x, z, b, HERO_R))) return true;
    if (L.props.some(p => inProp(x, z, p, HERO_R))) return true;
    if (L.cliffs.some(c => c.closed
      ? inPolygon(x, z, c.points) || distRing(x, z, c.points) < HERO_R
      : distPolyline(x, z, c.points) < CLIFF_HALF + HERO_R)) return true;
    for (const team of Object.keys(L.bases) as Team[]) {
      const b = baseParts(team, L);
      if (Math.hypot(x - b.fountain[0], z - b.fountain[1]) < 1.3 + HERO_R) return true;
      if (Math.hypot(x - b.shop[0], z - b.shop[1]) < 1.0 + HERO_R) return true;
    }
    return false;
  }
  function buildNav(): NavGrid {
    const { x, z } = L.bounds;
    return new NavGrid({ minX: -x - 0.5, maxX: x + 0.5, minZ: -z - 0.5, maxZ: z + 0.5, cell: 0.5, blocked });
  }
  /* Multiplies walking speed (heroes and monsters alike): a pure function of position, so client prediction
     slows a hero at the same step the server does. */
  function groundSpeed(x: number, z: number) {
    return L.fords.some(f => inRect(x, z, f)) ? FORD_SPEED : 1;
  }
  return { lineOfSight, visionPolygon, solid, solidNear, blocked, buildNav, groundSpeed };
}

export let { lineOfSight, visionPolygon, solid, solidNear, blocked, buildNav, groundSpeed } = terrain(LAYOUT);

/** Only core/map/runtime.ts's setActiveMap() calls this. */
export function bindLayout(map: MapData) {
  LAYOUT = layoutFromMap(map);
  ({ lineOfSight, visionPolygon, solid, solidNear, blocked, buildNav, groundSpeed } = terrain(LAYOUT));
}
